import { InfoField } from "$components/info-field";
import { Page } from "$components/page";
import { useUser } from "$global-providers/user-provider";
import { formatDate, formatUserName } from "$routes/inventory/_lib/formatting";
import InventoryTitleRow from "$routes/inventory/_lib/inventory-title-row";
import {
  AllStockTakeLotFieldsFragment,
  mapStockTakeLotsFragmentToRows,
} from "$routes/inventory/_lib/stock-take-lots-fragment";
import StockTakeSummaryTable from "$routes/inventory/_lib/stock-take-summary-table";
import { useFacilityLocationName } from "$routes/inventory/_lib/use-facility-locations";
import { linkToStockTakeHistory } from "$routes/inventory/links";
import { gql } from "@apollo/client";
import { Box } from "@material-ui/core";
import { StockTakeLotsSearchOrder } from "elephant-web/gql";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useHistoricStockTakeQuery } from "./__generated__/page.gql";

const PAGE_SIZE = 20;

gql`
  ${AllStockTakeLotFieldsFragment}

  query historicStockTake(
    $stockTakeId: ID!
    $facilityId: ID!
    $input: StockTakeLotsInput
    $options: StockTakeLotsOptions
  ) {
    stockTake(input: { stockTakeId: $stockTakeId, facilityId: $facilityId }) {
      locationId
      finalisedAt
      lots(input: $input, options: $options) {
        items {
          ...AllStockTakeLotFields
        }
      }
      finalisedBy {
        person {
          firstname
          lastname
        }
      }
      aggregateInfo {
        totalLotCount
        reviewedLotCount
        totalNewLotsAdded
        totalQuantityAdded
        totalQuantityRemoved
      }
    }
  }
`;

const HistoricStockTakeOverview = (): JSX.Element => {
  const { stockTakeId } = useParams<{ stockTakeId: string }>();
  const { facilityId } = useUser();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const { formatMessage } = useIntl();

  const { data, loading } = useHistoricStockTakeQuery({
    variables: {
      stockTakeId,
      facilityId,
      input: {
        reviewed: true,
      },
      options: {
        orderBy: StockTakeLotsSearchOrder.Name,
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
      },
    },
  });

  const {
    locationId,
    finalisedAt,
    aggregateInfo: { totalLotCount = 0, reviewedLotCount = 0 } = {},
  } = data?.stockTake || {};

  const locationName = useFacilityLocationName(locationId);
  const stockTakeRows = mapStockTakeLotsFragmentToRows(
    data?.stockTake?.lots.items ?? [],
  );
  return (
    <Page data-dense>
      <InventoryTitleRow
        title={formatMessage({
          id: "inventory.stockTake.historicStockTake.title",
          defaultMessage: "Stocktake Summary",
        })}
        backToLink={linkToStockTakeHistory()}
      >
        <Box display="flex">
          <InfoField
            id="location"
            label={formatMessage({
              id: "inventory.stockTake.historyStockTake.location",
              defaultMessage: "Location",
            })}
            value={locationName ?? ""}
            minWidth="300px"
          />
          <InfoField
            id="date"
            label={formatMessage({
              id: "inventory.stockTake.historyStockTake.date",
              defaultMessage: "Date",
            })}
            value={formatDate(finalisedAt)}
            width="120px"
          />
          <InfoField
            id="lotsUpdatedCount"
            label={formatMessage({
              id: "inventory.stockTake.historyStockTake.lotsUpdated",
              defaultMessage: "Lots Updated",
            })}
            value={`${reviewedLotCount}/${totalLotCount}`}
            width="120px"
          />
          <InfoField
            id="unitsRemoved"
            label={formatMessage({
              id: "inventory.stockTake.historyStockTake.unitsRemoved",
              defaultMessage: "Units Removed",
            })}
            value={data?.stockTake?.aggregateInfo?.totalQuantityRemoved ?? 0}
            width="130px"
          />
          <InfoField
            id="unitsAdded"
            label={formatMessage({
              id: "inventory.stockTake.historyStockTake.unitsAdded",
              defaultMessage: "Units Added",
            })}
            value={data?.stockTake?.aggregateInfo?.totalQuantityAdded ?? 0}
            width="130px"
          />
          <InfoField
            id="doneBy"
            label={formatMessage({
              id: "inventory.stockTake.historyStockTake.doneBy",
              defaultMessage: "Done By",
            })}
            value={formatUserName(data?.stockTake?.finalisedBy)}
            minWidth="200px"
          />
        </Box>
      </InventoryTitleRow>

      <StockTakeSummaryTable
        loading={loading}
        stockTakeRows={stockTakeRows}
        totalRows={totalLotCount}
        currentPage={currentPage}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />
    </Page>
  );
};

export default HistoricStockTakeOverview;
